// useResponsive — zentrale Breakpoint-/Orientierungs-Erkennung für die
// responsive Überarbeitung (Kontakte-Tabelle, Navigation, Detailansicht).
//
// Abgestimmte Schwellen (mit dem Team):
//   • Phone   (≤ 768px)            → Karten-Liste + Overlay-Navigation
//   • Tablet  (769px … 1024px)     → Tabelle (auch im Hochformat) + Icon-Rail-Navigation
//                                    + Rotations-Hinweis im Hochformat
//   • Desktop (> 1024px)           → Tabelle + voll ausgeklappte Navigation
//
// Hinweis: Ein erzwungenes Drehen (`screen.orientation.lock`) ist im Browser
// nicht zuverlässig möglich (iPad-Safari unterstützt es gar nicht), daher liefern
// wir nur die Orientierung und zeigen im Hochformat einen Hinweis an.

import { useEffect, useState } from "react";
import { alleFenster } from "../components/windowManager/browserFenster/fensterRegister";

export const BREAKPOINTS = {
  /** ≤ phone → Karten / Overlay-Navigation */
  phone: 768,
  /** ≤ tablet → Tabelle + Icon-Rail-Navigation */
  tablet: 1024,
} as const;

export type Orientation = "portrait" | "landscape";

/**
 * Phone ODER Tablet (≤ 1024px) anhand der reinen Viewport-Breite.
 * Pure Funktion (ohne DOM/Hook), damit die Schwelle an einer Stelle liegt und
 * vitest-testbar bleibt. Genutzt u.a. vom WindowManager (G2): dort werden
 * Fenster auf Phone UND Tablet als Vollbild statt frei/schwebend gerendert.
 */
export function isPhoneOrTabletWidth(width: number): boolean {
  return width <= BREAKPOINTS.tablet;
}

type Viewport = { width: number; height: number };

function readViewport(): Viewport {
  if (typeof window === "undefined") {
    // SSR-Fallback: Desktop annehmen, damit kein FOUC zur Karten-Ansicht entsteht.
    return { width: 1280, height: 800 };
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

export interface ResponsiveState {
  width: number;
  height: number;
  /** ≤ 768px — echte Handys: Karten-Ansicht, Overlay-Navigation. */
  isPhone: boolean;
  /** 769–1024px — Tablets: Tabelle + Icon-Rail, Rotations-Hinweis im Hochformat. */
  isTablet: boolean;
  /** ≤ 1024px — Phone ODER Tablet (G2: WindowManager rendert Fenster als Vollbild). */
  isPhoneOrTablet: boolean;
  /** > 1024px — Desktop: volle Tabelle + ausgeklappte Navigation. */
  isDesktop: boolean;
  orientation: Orientation;
  isPortrait: boolean;
}

export function useResponsive(): ResponsiveState {
  const [viewport, setViewport] = useState<Viewport>(readViewport);

  useEffect(() => {
    const onChange = () => setViewport(readViewport());
    alleFenster.addEventListener("resize", onChange);
    // `orientationchange` feuert auf iOS teils vor dem Layout-Update — der
    // zusätzliche resize-Listener fängt die finale Größe sicher ab.
    window.addEventListener("orientationchange", onChange);
    return () => {
      alleFenster.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
    };
  }, []);

  const { width, height } = viewport;
  const isPhone = width <= BREAKPOINTS.phone;
  const isTablet = width > BREAKPOINTS.phone && width <= BREAKPOINTS.tablet;
  const isPhoneOrTablet = isPhoneOrTabletWidth(width);
  const isDesktop = width > BREAKPOINTS.tablet;
  const orientation: Orientation = height >= width ? "portrait" : "landscape";

  return {
    width,
    height,
    isPhone,
    isTablet,
    isPhoneOrTablet,
    isDesktop,
    orientation,
    isPortrait: orientation === "portrait",
  };
}

export default useResponsive;
